<template>
  <teleport to="body">
    <transition name="info-modal" appear>
      <div v-if="modelValue" class="modal-overlay info-modal-overlay" @click.self="onOverlayClick">
  <div ref="modalContent" class="modal-content info-modal-content" role="dialog" aria-modal="true" :aria-labelledby="labelId">
      <div class="modal-sub-header">
        <div class="sub-header">
          <ModalHeader>
            <template #default>
              <h2 class="page-title" :id="labelId">{{ title }}</h2>
            </template>
            <!-- no header actions for InfoModal; footer provides close button -->
          </ModalHeader>
        </div>
        <hr class="divider-line" />
      </div>

      <div class="modal-body" style="padding: 12px 18px;">
        <slot>
          <p class="info-modal-default-text">
            {{ defaultContent }}
          </p>
        </slot>
      </div>

      <div class="modal-actions" style="padding: 12px 18px; display:flex; gap:12px; justify-content:flex-end;">
        <slot name="actions" :close="close">
          <BaseButton v-if="showLearnLink" title="آموزش کامل" variant="primary" type="button" @click="openLearnLink" />
          <BaseButton title="بستن" variant="cancel" type="button" @click="close" />
        </slot>
      </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, watch, onUnmounted, defineProps, defineEmits, nextTick } from 'vue';
import ModalHeader from '@/components/Common/ModalHeader.vue';
import BaseButton from '@/components/UiCompunents/BaseButton.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'راهنما',
  },
  infoLearnUrl: {
    type: String,
    default: 'https://www.sinapardazesh.ir/matab-learning-web/',
  },
  showLearnLink: {
    type: Boolean,
    default: true,
  },
  defaultContent: {
    type: String,
    default: '',
  },
  closeOnOverlay: {
    type: Boolean,
    default: true,
  },
  id: {
    type: String,
    default: 'info-modal',
  },
});

const emit = defineEmits(['update:modelValue', 'close']);

const isOpen = ref(props.modelValue);
const modalContent = ref(null);
let openerElement = null;

watch(() => props.modelValue, (val) => {
  isOpen.value = val;
});

function close() {
  emit('update:modelValue', false);
  emit('close');
}

function onOverlayClick() {
  if (props.closeOnOverlay) close();
}

function openLearnLink() {
  if (!props.infoLearnUrl) return;
  window.open(props.infoLearnUrl, '_blank', 'noopener,noreferrer');
}

function handleInfoKeydown(e) {
  if (e.key === 'Escape' || e.key === 'Esc') close();
}

// Focus trap: keep focus inside the modal while open
const focusableSelectors = 'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [contenteditable], [tabindex]:not([tabindex="-1"])';

/*
  توضیح (فارسی): Focus trap چیست و اینجا چگونه کار می‌کند
  - وقتی مودال باز می‌شود، باید صفحه کلید (Tab/Shift+Tab) را داخل مودال محدود کنیم تا کاربر با زدن Tab نتواند به خارج مودال برود.
  - برای این کار ما لیست المان‌های focusable در داخل مودال را می‌گیریم و اگر کاربر Tab را روی آخرین المان زد، دوباره به اولین المان باز می‌گردیم (و بالعکس برای Shift+Tab).
  - همچنین هنگام باز شدن، عنصر باز کننده مودال (openerElement) ذخیره می‌شود تا بعد از بستن مودال، فوکوس به همان المان بازگردانده شود.
*/

function getFocusableElements() {
  if (!modalContent?.value) return [];
  return Array.from(modalContent.value.querySelectorAll(focusableSelectors)).filter(
    (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
  );
}

function handleTabKeydown(e) {
  if (e.key !== 'Tab') return;
  const focusable = getFocusableElements();
  if (focusable.length === 0) {
    // No focusable elements, keep focus on modal content container
    e.preventDefault();
    modalContent.value && modalContent.value.focus();
    return;
  }

  const firstElem = focusable[0];
  const lastElem = focusable[focusable.length - 1];
  const shift = e.shiftKey;

  if (!shift && document.activeElement === lastElem) {
    e.preventDefault();
    firstElem.focus();
  } else if (shift && document.activeElement === firstElem) {
    e.preventDefault();
    lastElem.focus();
  }
}

watch(isOpen, async (val) => {
  if (val) {
    // store opener so we can return focus when modal closes
    openerElement = document.activeElement;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleInfoKeydown);
    window.addEventListener('keydown', handleTabKeydown);
    // focus close button after render
    await nextTick();
    const btn = document.querySelector('#' + labelId + ' ~ .modal-actions .base-button');
    if (btn) {
      btn.focus();
    } else if (modalContent.value) {
      // focus first focusable element or modal content itself
      const focusable = getFocusableElements();
      if (focusable.length > 0) focusable[0].focus();
      else {
        modalContent.value.setAttribute('tabindex', '-1');
        modalContent.value.focus();
      }
    }
  } else {
    document.body.style.overflow = 'auto';
    window.removeEventListener('keydown', handleInfoKeydown);
    window.removeEventListener('keydown', handleTabKeydown);
    // return focus to opener if possible
    try {
      if (openerElement && typeof openerElement.focus === 'function') openerElement.focus();
    } catch (e) {
      // ignore if openerElement lost focusability
    }
  }
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleInfoKeydown);
  document.body.style.overflow = 'auto';
});

// For ARIA label
const labelId = `info-modal-${props.id}`;
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(var(--text-color-secondary-rgb), 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 30000 !important;
  -webkit-backdrop-filter: saturate(180%) blur(5px) !important;
  backdrop-filter: saturate(180%) blur(5px) !important;
}

.info-modal-content {
  width: auto;
  max-width: 720px;
  min-width: 320px;
  height: auto;
  max-height: 80vh;
  border-radius: 12px;
  padding: 18px 20px;
  box-shadow: 0 8px 30px rgba(var(--text-color-secondary-rgb), 0.12);
  background-color: var(--background-color); /* use theme background color */
  color: var(--text-color-primary);
}
.info-modal-content .modal-sub-header {
  padding-top: 8px;
  background: transparent;
}
.info-modal-content .modal-actions {
  background: transparent;
}
.info-modal-content .modal-body {
  padding: 6px 2px 12px 2px;
  overflow-y: auto;
  font-size: calc(0.7vw + 8px);
  direction: rtl;
  text-align: right;
}
.info-modal-content .modal-body :deep(*) {
  font-size: inherit !important;
  direction: rtl !important;
  text-align: right;
}
.info-modal-default-text { white-space: pre-line; }
@media (max-width: 768px) {
  .info-modal-overlay { align-items: flex-end !important; padding: 0 12px 12px !important; }
  .info-modal-content { width: 100% !important; max-width: 100% !important; margin: 0 !important; border-radius: 12px !important; padding: 12px !important; box-shadow: 0 -6px 30px rgba(var(--text-color-secondary-rgb), 0.12) !important; transform: none !important; }
}

/* Ensure the info modal is vertically centered on all pages, regardless of other modal styles */
.info-modal-overlay { align-items: center !important; justify-content: center !important; }

/* Smooth opening/closing transitions */
.info-modal-enter-active, .info-modal-leave-active {
  transition: opacity 220ms cubic-bezier(.2, .9, .2, 1);
}
.info-modal-enter-from, .info-modal-leave-to {
  opacity: 0;
}
.info-modal-enter-to, .info-modal-leave-from {
  opacity: 1;
}
/* Subtle content motion for a softer open */
.info-modal-enter-active .info-modal-content, .info-modal-leave-active .info-modal-content {
  transition: transform 220ms cubic-bezier(.2, .9, .2, 1), opacity 220ms linear;
}
.info-modal-enter-from .info-modal-content, .info-modal-leave-to .info-modal-content {
  transform: translateY(-10px) scale(0.98);
  opacity: 0.96;
}
.info-modal-enter-to .info-modal-content, .info-modal-leave-from .info-modal-content {
  transform: translateY(0) scale(1);
  opacity: 1;
}

/* Specific rule: align ModalHeader (title) to the right inside InfoModal */
.info-modal-content :deep(.modal-header) {
  direction: rtl !important; /* ensure RTL alignment inside header */
  margin: 10px !important; /* add 10px margin to modal header */
  background: transparent !important;
}
.info-modal-content :deep(.modal-header) :deep(.header-right) {
  /* make sure header-right floats to the far-right and the icon is left of text */
  margin-left: auto !important;
  flex-direction: row-reverse !important; /* make the title appear on the right and icon on the left */
  gap: 8px !important;
  align-items: center !important;
  width: auto !important;
}
.info-modal-content :deep(.modal-header) :deep(.modal-title) {
  text-align: right !important;
  width: auto !important;
}
.info-modal-content :deep(.modal-header .modal-title h2) {
  text-align: right !important;
  margin: 0 !important;
  font-size: calc(0.8vw + 15px) !important;
}
.info-modal-content :deep(.modal-header) :deep(.header-left) { display: none !important; }
.info-modal-content :deep(.modal-info-icon) { display: none !important; }

.modal-close-btn {
  border: none;
  font-size: calc(0.3vw + 12px);
  color: var(--text-color-secondary);
  background-color: transparent;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cancel-button {
  border-radius: 10px;
  border: none;
  padding: 7px 20px;
  background-color: var(--background-color-button2);
  color: var(--text-color-thirdly);
  cursor: pointer;
}

/* Scrollbar for the Info Modal body uses theme variables */
.info-modal-content .modal-body {
  scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track);
  scrollbar-width: thin;
}
.info-modal-content .modal-body::-webkit-scrollbar {
  width: 10px;
  background: var(--scrollbar-track);
}
.info-modal-content .modal-body::-webkit-scrollbar-thumb {
  background: var(--scrollbar-thumb);
  border-radius: 8px;
}
.info-modal-content .modal-body::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--text-color-secondary-rgb), 0.18);
}

</style>
